import { useEffect, useMemo, useState } from "react"

import { useTransportPaginatedQuery } from "@/transport"

const SCHEDULED_RUNS_PAGE_SIZE = 100

export interface TemplateDeleteBlockingRun {
  displayName: string
  id: string
}

export type TemplateDeleteAvailability =
  | { status: "idle" }
  | { status: "checking" }
  | { status: "available" }
  | { status: "blocked"; scheduledRuns: TemplateDeleteBlockingRun[] }
  | { status: "error" }

interface CompletedCheck {
  availability: TemplateDeleteAvailability
  checkKey: number
  templateId: string
}

export function useTemplateDeleteAvailability(
  templateId: string | null | undefined,
  checkKey: number | null,
): TemplateDeleteAvailability {
  const [completedCheck, setCompletedCheck] = useState<CompletedCheck | null>(
    null,
  )
  const request = useMemo(
    () => ({
      enabled: true,
      page_size: SCHEDULED_RUNS_PAGE_SIZE,
      template_id: templateId ?? "",
    }),
    [templateId],
  )
  const { refetch } = useTransportPaginatedQuery({
    combinePages: (pages) => ({
      scheduled_runs: pages.flatMap((page) => page.scheduled_runs),
    }),
    getNextRequest: (page, previousRequest) =>
      page.next_page_token
        ? {
            ...(previousRequest ?? request),
            page_token: page.next_page_token,
          }
        : null,
    operation: "listScheduledRuns",
    request,
    options: {
      enabled: false,
      staleTime: 0,
    },
  })

  useEffect(() => {
    if (checkKey === null || !templateId) {
      return
    }

    let cancelled = false

    void refetch().then(({ data, error }) => {
      if (cancelled) {
        return
      }

      const scheduledRuns = (data?.scheduled_runs ?? []).map((run) => ({
        displayName: run.display_name,
        id: run.id,
      }))
      const availability: TemplateDeleteAvailability = error
        ? { status: "error" }
        : scheduledRuns.length > 0
          ? { status: "blocked", scheduledRuns }
          : { status: "available" }

      setCompletedCheck({ availability, checkKey, templateId })
    })

    return () => {
      cancelled = true
    }
  }, [checkKey, refetch, templateId])

  if (checkKey === null || !templateId) {
    return { status: "idle" }
  }
  if (
    completedCheck?.checkKey !== checkKey ||
    completedCheck.templateId !== templateId
  ) {
    return { status: "checking" }
  }
  return completedCheck.availability
}
